import { useState, useMemo, useEffect, useRef } from 'preact/hooks'

import clsx from 'clsx'

import { useQuickAdd, isVariantOutOfStock, getVariantSizeLabel } from 'libs/athos/hooks/useQuickAdd'
import { SIZE_SELECTED_CPROPS, PERSIST_DEBOUNCE_MS } from 'libs/swym/swym-entry'
import LoaderSpin from 'libs/athos/components/LoaderSpin'

// Wishlist-grid size selector. Mirrors the PDP grid variant selector
// (src/components/snippets/fields/field-variant-selector-grid.liquid /
// variant-selector.ts): same `group/option` + `.active` border toggle, same
// sold-out diagonal strike, same `grow w-full max-w-grid-1/7` per-button cap
// so up to 7 sizes sit on one row without wrapping — but as a controlled
// Preact button (no hidden <select>/custom element needed here), followed by
// its own "Add to Bag" button rather than adding on click. Inner padding is
// tighter than the PDP's (13px) since this card is far narrower than the
// PDP's variant-selector column.
export function WishlistSizeSelector({ result, savedVariantId = null, entry = null, onPersistEntry = null, className = '' }) {
  const { isAvailable, isComingSoon, addingId, addVariant } = useQuickAdd(result)

  const variants = result?.variants ?? []

  // The size the shopper saved from the PDP (Swym's `epi`, numeric) matched
  // against the feed's variant ids, which arrive as strings from `ss_sizes`
  // (adaptSizesFromFeed in libs/athos/helpers/normalisers.ts) — coerce both sides.
  // A saved size that's sold out or no longer in the feed stays unselected: the
  // chip already renders with the sold-out strike and the button already reads
  // "Select Size", which is the right message.
  const savedVariant = useMemo(() => {
    if (!savedVariantId) return null
    const match = variants.find((variant) => Number(variant.id) === Number(savedVariantId))
    return match && !isVariantOutOfStock(match) ? match : null
  }, [variants, savedVariantId])

  const [selectedVariantId, setSelectedVariantId] = useState(savedVariant?.id ?? null)

  // Variants can arrive after mount (the card re-renders once the Athos result
  // resolves, and swatch switching swaps `result` entirely), so adopt the saved
  // size whenever it appears rather than only on first render.
  useEffect(() => {
    if (savedVariant) setSelectedVariantId(savedVariant.id)
  }, [savedVariant])

  // Persist a chip tap to Swym: it's always a deliberate size choice, so the
  // recreated entry carries `cprops` even if this card's entry didn't before
  // (a plain PLP-saved card gets "upgraded" to size-explicit the first time a
  // size is picked on it). Debounced so switching through several chips fires
  // one replace, not one per tap; only runs on the wishlist page, where
  // `entry`/`onPersistEntry` are supplied — PLP/search/recs cards never pass
  // either, so this is a no-op there regardless of what's clicked.
  const persistTimer = useRef(null)
  useEffect(() => {
    if (!entry || !onPersistEntry) return
    if (!selectedVariantId || Number(selectedVariantId) === Number(savedVariantId)) return

    clearTimeout(persistTimer.current)
    persistTimer.current = setTimeout(async () => {
      const newEntry = { epi: Number(selectedVariantId), empi: entry.empi, du: entry.du, cprops: SIZE_SELECTED_CPROPS }
      const ok = await onPersistEntry(entry, newEntry)
      if (!ok) {
        console.warn('[wishlist] size change failed to save, reverting to the saved size')
        setSelectedVariantId(savedVariant?.id ?? null)
      }
    }, PERSIST_DEBOUNCE_MS)

    return () => clearTimeout(persistTimer.current)
  }, [selectedVariantId, savedVariantId, entry, onPersistEntry, savedVariant])

  const selectedVariant = variants.find((variant) => variant.id === selectedVariantId) ?? null
  const isAdding = addingId != null
  const isOnlySize = variants.length === 1

  if (!isAvailable) {
    return (
      <div className={clsx('flex w-full', className)}>
        <span
          className="btn btn-s btn-fill w-full border-borders-default text-borders-default disabled:text-type-disabled disabled:bg-button-disabled disabled:border-button-disabled"
          data-ui="disabled"
        >
          Sold out
        </span>
      </div>
    )
  }

  const handleAdd = async (event) => {
    if (!selectedVariant) return
    await addVariant(event, selectedVariant)
    // No reset: the selection now persists to Swym (see the effect above), so
    // the saved size IS whatever was last picked — there's nothing to fall
    // back to after adding.
  }

  return (
    <div className={clsx('flex w-full flex-col gap-y-1', className)}>
      <div className="flex flex-wrap gap-x-0.5 gap-y-0.25">
        {variants.map((variant) => {
          const outOfStock = isVariantOutOfStock(variant)
          const isSelected = variant.id === selectedVariantId
          const sizeLabel = getVariantSizeLabel(variant)
          return (
            <button
              key={variant.id}
              type="button"
              tabIndex={outOfStock ? -1 : 0}
              className={clsx(
                'group/option grow w-full text-xs !px-0',
                isOnlySize ? 'max-w-grid-1/5' : 'max-w-grid-1/4 lg:max-w-grid-1/7',
                outOfStock && 'pointer-events-none',
                isSelected && 'active'
              )}
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                setSelectedVariantId(variant.id)
              }}
              disabled={outOfStock}
              aria-pressed={isSelected}
              aria-label={`Select size ${sizeLabel}`}
            >
              <span
                className={clsx(
                  'btn px-[2px] py-[12px] group-[.active]/option:border-brand-black',
                  outOfStock
                    ? "btn-border relative overflow-hidden border-borders-default text-borders-default after:pointer-events-none after:absolute after:inset-0 after:content-[''] after:bg-[linear-gradient(to_top_right,transparent_calc(50%_-_0.5px),currentColor_calc(50%_-_0.5px),currentColor_calc(50%_+_0.5px),transparent_calc(50%_+_0.5px))]"
                    : 'btn-primary btn-border'
                )}
              >
                <span className="overflow-hidden text-ellipsis whitespace-nowrap text-xs">{sizeLabel}</span>
              </span>
            </button>
          )
        })}
      </div>

      {/* Coming Soon disables the CTA but leaves the size chips live —
          the shopper can still change which size they have saved for launch,
          which is the whole point of holding it on the wishlist. */}
      {isComingSoon ? (
        <span
          className="btn btn-m btn-fill w-full border-borders-default text-type-disabled bg-button-disabled border-button-disabled"
          data-ui="disabled"
        >
          Coming Soon
        </span>
      ) : (
        <button
          type="button"
          className="btn btn-m btn-fill btn-primary relative w-full"
          onClick={handleAdd}
          disabled={!selectedVariant || isAdding}
          aria-busy={isAdding}
          aria-label={selectedVariant ? 'Add to Bag' : 'Select a size'}
        >
          <span className={isAdding ? 'opacity-80' : ''}>{selectedVariant ? 'Add to Bag' : 'Select Size'}</span>

          {isAdding && <LoaderSpin wrapperClass="bg-transparent" spinClass="border-t-brand-paper" />}
        </button>
      )}
    </div>
  )
}
